import { useMemo } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { motion } from "framer-motion";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, Legend,
} from "recharts";

const Card = ({ title, children, className = "", testid }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
    className={`bg-white rounded-xl border border-slate-200 shadow-sm p-6 ${className}`} data-testid={testid}>
    <h3 className="font-heading font-semibold text-slate-900 mb-5">{title}</h3>
    {children}
  </motion.div>
);

const parseDate = (s) => {
  if (!s || s === "—") return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Reports() {
  const { tasks, notes, events, renewals, documents } = useFgp();

  const r = useMemo(() => {
    const now = new Date();
    const in60 = new Date(now); in60.setDate(now.getDate() + 60);
    const in30 = new Date(now); in30.setDate(now.getDate() + 30);

    // Tasks by status
    const statusDefs = [
      { key: "todo", name: "To do", color: "#2D74B4" },
      { key: "in-progress", name: "In progress", color: "#D99A00" },
      { key: "done", name: "Done", color: "#28A263" },
      { key: "overdue", name: "Overdue", color: "#D94255" },
    ];
    const tasksByStatus = statusDefs.map((s) => ({
      name: s.name, color: s.color,
      value: tasks.filter((t) => t.status === s.key).length,
    }));
    const doneCount = tasks.filter((t) => t.status === "done").length;
    const overdueTasks = tasks.filter((t) => t.status === "overdue").length;

    // Renewals
    const overdueRenewals = renewals.filter((x) => x.status === "overdue").length;
    const onTrackRenewals = renewals.length - overdueRenewals;
    const dueSoon = renewals.filter((x) => {
      const d = parseDate(x.due);
      return d && d >= now && d <= in60;
    }).length;

    // Upcoming events (next 30 days)
    const upcomingEvents = events.filter((e) => {
      const d = parseDate(e.date);
      return d && d >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) && d <= in30;
    }).length;

    // Household activity — last 4 weeks (W1 oldest → W4 this week)
    const weekBuckets = [0, 1, 2, 3].map((i) => ({ week: `W${i + 1}`, tasks: 0, events: 0, notes: 0 }));
    const bucketOf = (d) => {
      const days = Math.floor((now - d) / 86400000);
      if (days < 0 || days > 27) return -1;
      return 3 - Math.floor(days / 7); // 0..3 → W1..W4
    };
    tasks.forEach((t) => { const b = bucketOf(parseDate(t.due)); if (b >= 0) weekBuckets[b].tasks++; });
    events.forEach((e) => { const b = bucketOf(parseDate(e.date)); if (b >= 0) weekBuckets[b].events++; });
    notes.forEach((n) => { const b = bucketOf(parseDate(n.date)); if (b >= 0) weekBuckets[b].notes++; });

    // Events by category
    const evCat = {};
    events.forEach((e) => { const c = e.category || "Other"; evCat[c] = (evCat[c] || 0) + 1; });
    const eventsByCategory = Object.entries(evCat).map(([category, count]) => ({ category, count }));

    // Renewals timeline (by month, next 6 months incl. overdue)
    const timelineMonths = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      timelineMonths.push({ key: `${d.getFullYear()}-${d.getMonth()}`, month: MONTHS[d.getMonth()], count: 0 });
    }
    renewals.forEach((x) => {
      const d = parseDate(x.due);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const slot = timelineMonths.find((m) => m.key === key);
      if (slot) slot.count++;
    });

    const totalTrackable = renewals.length + documents.length;
    const okItems = onTrackRenewals + documents.filter((d) => d.status === "valid").length;
    const onTrackPct = totalTrackable ? Math.round((okItems / totalTrackable) * 100) : 100;

    const voiceNotes = notes.filter((n) => n.type === "voice").length;

    return {
      summary: [
        { label: "Tasks completed", value: doneCount, sub: `of ${tasks.length} total` },
        { label: "Notes captured", value: notes.length, sub: `${voiceNotes} voice` },
        { label: "Renewals on track", value: `${onTrackRenewals} / ${renewals.length}`, sub: overdueRenewals ? `${overdueRenewals} overdue` : "all on track" },
        { label: "Upcoming events", value: upcomingEvents, sub: "next 30 days" },
      ],
      tasksByStatus,
      householdActivity: weekBuckets,
      eventsByCategory,
      renewalsTimeline: timelineMonths,
      snapshot: [
        { label: "Overdue items", value: String(overdueRenewals + overdueTasks), tone: "text-rose-600 bg-rose-50 border-rose-200" },
        { label: "Due within 60 days", value: String(dueSoon), tone: "text-amber-600 bg-amber-50 border-amber-200" },
        { label: "On track", value: `${onTrackPct}%`, tone: "text-emerald-600 bg-emerald-50 border-emerald-200" },
      ],
    };
  }, [tasks, notes, events, renewals, documents]);

  const hasActivity = r.householdActivity.some((w) => w.tasks || w.events || w.notes);

  return (
    <div>
      <PageHeader title="Reports" subtitle="A richer view of your activity, tasks, renewals and events — derived live from your workspace." />

      {/* Weekly summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {r.summary.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={`summary-${i}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
            <p className="mt-2 font-heading text-2xl font-semibold text-slate-900">{s.value}</p>
            <p className="mt-1 text-xs font-medium text-slate-400">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tasks by status — pie */}
        <Card title="Tasks by status" testid="chart-tasks">
          {r.tasksByStatus.some((t) => t.value) ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={r.tasksByStatus.filter((t) => t.value)} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} isAnimationActive={false}>
                    {r.tasksByStatus.filter((t) => t.value).map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {r.tasksByStatus.map((t) => (
                  <div key={t.name} className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} /> {t.name} <span className="text-slate-400 ml-auto">{t.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">No tasks yet.</div>}
        </Card>

        {/* Household activity — area */}
        <Card title="Activity (last 4 weeks)" className="lg:col-span-2" testid="chart-activity">
          {hasActivity ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={r.householdActivity} margin={{ left: -20 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2D74B4" stopOpacity={0.3} /><stop offset="95%" stopColor="#2D74B4" stopOpacity={0} /></linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#28A263" stopOpacity={0.3} /><stop offset="95%" stopColor="#28A263" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="week" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="tasks" stroke="#2D74B4" strokeWidth={2} fill="url(#g1)" isAnimationActive={false} />
                <Area type="monotone" dataKey="events" stroke="#28A263" strokeWidth={2} fill="url(#g2)" isAnimationActive={false} />
                <Area type="monotone" dataKey="notes" stroke="#D99A00" strokeWidth={2} fillOpacity={0} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-sm text-slate-400">No recent activity yet — add tasks, events or notes to see trends.</div>}
        </Card>

        {/* Events by category — bar */}
        <Card title="Events overview" className="lg:col-span-2" testid="chart-events">
          {r.eventsByCategory.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={r.eventsByCategory} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="count" fill="#2D74B4" radius={[6, 6, 0, 0]} barSize={36} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">No events yet.</div>}
        </Card>

        {/* Renewals timeline — bar */}
        <Card title="Renewals timeline" testid="chart-renewals">
          {r.renewalsTimeline.some((m) => m.count) ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={r.renewalsTimeline} layout="vertical" margin={{ left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={36} />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="count" fill="#D99A00" radius={[0, 6, 6, 0]} barSize={20} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[260px] flex items-center justify-center text-sm text-slate-400">No upcoming renewals.</div>}
        </Card>
      </div>

      {/* Alerts / overdue snapshot */}
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {r.snapshot.map((s) => (
          <div key={s.label} className={`rounded-xl border p-5 ${s.tone}`}>
            <p className="font-heading text-3xl font-semibold">{s.value}</p>
            <p className="text-sm font-medium mt-1">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
